import type {
  QuestionnaireSection,
  QuestionnairePage,
  OnboardingIntroPage,
  OnboardingTrialPage,
  ProductDemoPage,
  LearningSetupSummaryPage,
  QuestionnaireChoice,
  PlanSelection20676Page,
  PaymentCapture20676Page,
} from './onboarding.types'
import {
  ONBOARDING_DEFAULT_SUBJECT,
  ONBOARDING_WORKING_TOWARDS_BY_SUBJECT,
} from './onboarding.personalization'
import { getKnownContentOriginSlug } from '@/helpers/billingSourceBook'
import { trialByPlanV2_18369Treatment } from '@/services/ab/18369_trial_by_plan'
import {
  onboardingPlanCcCaptureV2_20676EVariant,
  sampleFirstOnboardingV10_20710Variant,
} from '@/services/ab/experiments'
import { submitTimeOfDay } from './onboarding'
import { notificationService } from '@/services/notification'
import { ContentStats } from '@/helpers/contentStats'

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const introPages: OnboardingIntroPage[] = [
  {
    title: "The <span>world's largest</span> summary library",
    image: 'intro-1.svg',
  },
  {
    title: '<span>Key points with no fluff</span> from nonfiction bestsellers',
    image: 'intro-2.svg',
  },
  {
    title: '<span>Read or listen</span> on desktop, mobile or Kindle',
    image: 'intro-3.svg',
  },
]

export const signupBannerPages: OnboardingIntroPage[] = introPages

const productDemoPage: ProductDemoPage = {
  id: 'product-demo',
  type: 'ProductDemo',
  title: 'Why are our summaries the highest-quality?',
  content: [
    'Hyper-focused on giving useful info to help you achieve your goals',
    '1-page summaries: get actionable insights fast',
    'Chapter summaries: go deeper and efficiently learn details that matter',
    'Expert commentary: outside context & alternate viewpoints',
    'Audio narration: fully voiced book summaries',
  ],
  animationMobile: {
    src: 'product-demo-mobile.riv',
    width: 324,
    height: 696,
  },
  animationDesktop: {
    src: 'product-demo-desktop.riv',
    width: 792,
    height: 482,
  },
  hideContinueButton: true,
}

const learningSetupSummaryPage: LearningSetupSummaryPage = {
  id: 'learning-setup-summary',
  type: 'LearningSetupSummary',
  hideHeader: true,
  title: 'Here’s how we’ll optimize your Shortform experience',
  subtitle: 'Your learning setup',
}

/**
 * Plan/card capture pages for exp_onboarding_plan_cc_capture_v2_20676 (#20676),
 * variant B: shown right after email capture, before the rest of
 * onboarding. The static `title` here is just a placeholder to satisfy the
 * required field -- PlanSelection20676.vue computes its own dynamic title
 * based on content origin.
 */
const planSelection20676Page: PlanSelection20676Page = {
  id: 'plan-selection-20676',
  type: 'PlanSelection20676',
  title: `${ContentStats.guides}+ guides are waiting for you!`,
  hideContinueButton: true,
}

const paymentCapture20676Page: PaymentCapture20676Page = {
  id: 'payment-capture-20676',
  type: 'PaymentCapture20676',
  title: `${ContentStats.guides}+ guides are waiting for you!`,
  hideContinueButton: true,
}

/**
 * Treatment screens for exp_samplefirst_onboarding_v10_20710.
 */
function bookSelection20710Page(id: string): QuestionnairePage {
  return {
    id,
    type: 'BookSelection20710',
    title: 'Which book do you want to start with?',
    subtitle: "Pick one and we'll build your path.",
    hideContinueButton: true,
  }
}

function buildSampleFirst20710Pages(): QuestionnaireSection[] {
  const hasEntryBook = Boolean(getKnownContentOriginSlug())
  const pages: QuestionnairePage[] = [
    {
      id: 'welcome-20710',
      type: 'Welcome20710',
      title: 'Welcome',
    },
  ]

  if (hasEntryBook) {
    pages.push(
      {
        id: 'book-validation-20710',
        type: 'BookValidation20710',
        title: 'Book validation',
      },
      bookSelection20710Page('book-selection-20710-after-validation'),
    )
  }

  pages.push(
    {
      id: 'what-brings-you-here',
      type: 'Options',
      title: hasEntryBook
        ? 'What else are you here for?'
        : 'What brings you here today?',
      subtitle: "Let's start with the big picture.",
      options: [
        { label: 'A specific book, article, or podcast' },
        { label: 'A subject I want to get into' },
        { label: 'Improving a certain skill' },
        { label: 'Reading more regularly' },
        { label: 'Not sure yet, show me around' },
      ],
    },
    bookSelection20710Page('book-selection-20710'),
    {
      id: 'options-picker-20710-topic',
      type: 'OptionsPicker20710',
      title: 'Topic picker',
    },
    {
      id: 'options-picker-20710-skill',
      type: 'OptionsPicker20710',
      title: 'Skill picker',
    },
    {
      id: 'habit-options-20710',
      type: 'HabitOptions20710',
      title: 'Habit setup',
    },
    {
      id: 'book-preview-20710',
      type: 'BookPreview20710',
      title: 'Guide preview',
    },
    bookSelection20710Page('book-selection-20710-after-preview'),
    {
      id: 'book-demo-20710',
      type: 'BookDemo20710',
      title: 'Demo',
    },
  )

  return [
    {
      id: 'sample-first-20710',
      title: 'Onboarding',
      pages,
    },
  ]
}

export function buildQuestionnairePages(): QuestionnaireSection[] {
  if (sampleFirstOnboardingV10_20710Variant() === 'B') {
    return buildSampleFirst20710Pages()
  }

  const showPlanCardCapture20676 =
    onboardingPlanCcCaptureV2_20676EVariant() === 'B'

  return [
    ...(showPlanCardCapture20676
      ? [
          {
            id: 'plan-card-capture-20676',
            title: 'Plan & Card',
            hideStepperSectionName: true,
            pages: [planSelection20676Page, paymentCapture20676Page],
          },
        ]
      : []),
    {
      id: '_intro',
      title: '_intro',
      excludeFromStepper: true,
      pages: [
        {
          id: 'welcome',
          type: 'Welcome',
          title: "The world's highest-quality summaries",
          introTitle: 'Welcome to Shortform',
          subtitle: `<span>Over ${ContentStats.books} books</span> across dozens of categories`,
          hideContinueButton: true,
          image: {
            src: 'mascot-welcome.svg',
            width: 405,
            height: 422,
          },
        },
        {
          id: 'personalize',
          type: 'Text',
          title:
            'Personalize your Shortform experience in <span>2 minutes</span>',
          subtitle:
            "We'll recommend content to help you quickly achieve your learning goals.",
          animation: {
            src: 'personalize.riv',
            width: 375,
            height: 375,
          },
          textSize: 'large',
          showLogo: true,
        },
      ],
    },
    {
      id: 'learning-setup',
      title: 'Questionnaire',
      pages: [
        {
          id: 'what-brings-you-here',
          type: 'Options',
          title: 'What brings you here today?',
          subtitle: "Let's start with the big picture.",
          options: [
            { label: 'Check out a specific summary' },
            { label: 'Work toward a personal goal' },
            { label: 'Explore a topic I care about' },
            { label: 'Build a reading habit' },
            { label: 'Just browsing' },
          ],
        },
        {
          id: 'what-subject-interests-you',
          type: 'Options',
          title: 'What subject interests you the most?',
          subtitle: 'Good to know.',
          options: [
            { label: 'Getting ahead professionally' },
            { label: 'Becoming a better partner or parent' },
            { label: 'Taking control of my finances' },
            { label: 'Being more focused and productive' },
            { label: 'Improving my health and wellness' },
            { label: 'Staying curious and continuing to learn' },
          ],
        },
        {
          id: 'what-are-you-working-towards',
          type: 'DynamicOptions',
          sourcePageId: 'what-subject-interests-you',
          defaultSourceSubject: ONBOARDING_DEFAULT_SUBJECT,
          title: 'What are you working towards?',
          subtitle: 'Choose the goal that fits best right now.',
          optionsBySubject: ONBOARDING_WORKING_TOWARDS_BY_SUBJECT,
        },
        {
          id: 'what-gets-in-your-way',
          type: 'Options',
          title: 'What usually makes it hard to read more?',
          subtitle: 'Out of curiosity,',
          options: [
            { label: 'My schedule is a circus' },
            { label: 'Reading puts me to sleep' },
            { label: 'Nothing grabs my attention' },
            { label: 'I prefer other forms of media' },
            { label: "I'm already a reading legend" },
          ],
        },
        {
          id: 'personalized-book-selection',
          type: 'PersonalizedBooks',
          title: 'Does this book speak to you?',
          hideContinueButton: true,
          content: ['book-0', 'book-1', 'book-2', 'book-3', 'book-4'],
        },
        {
          id: 'how-do-you-learn',
          type: 'Options',
          title: 'Do you prefer to read, listen, or get hands-on?',
          subtitle: 'Amazing. This is really helpful.',
          options: [
            { label: 'Read' },
            { label: 'Listen' },
            { label: 'Community-based exercises' },
            { label: 'A bit of each' },
          ],
        },
        {
          id: 'how-much-time',
          type: 'Options',
          title: 'How much time can you dedicate to learning each day?',
          subtitle: "Let's create a habit.",
          options: [
            { label: '30 minutes a day' },
            { label: '5 minutes a day' },
            { label: '1 hour or more' },
            { label: '15 minutes a day' },
          ],
        },
        {
          id: 'what-time-of-the-day',
          type: 'Options',
          title: 'When do you learn best?',
          subtitle:
            "We'll remind you then to build a consistent learning practice",
          options: [
            { label: 'Morning: 9:00 AM', icon: 'sun.svg' },
            { label: 'Midday: 12:00 PM', icon: 'meal.svg' },
            { label: 'Afternoon: 3:00 PM', icon: 'coffee.svg' },
            { label: 'Evening: 6:00 PM', icon: 'meditation.svg' },
            { label: 'Before Bed: 9:00 PM', icon: 'moon.svg' },
          ],
          async onSubmit(choice?: QuestionnaireChoice): Promise<void> {
            notificationService.init(true)
            await submitTimeOfDay(choice as string | undefined)
          },
        },
        {
          id: 'does-this-sound-interesting',
          type: 'Options',
          title: 'Do these sound interesting to you?',
          subtitle: 'Shortform is way more than just book summaries.',
          options: [
            { label: 'Podcast summaries' },
            { label: 'Article summaries' },
            { label: "I'm interested in both" },
          ],
        },
        {
          id: 'where-do-you-use-it',
          type: 'Options',
          title:
            'Are you using Shortform for work, school, or just for yourself?',
          subtitle: 'One last thing.',
          options: [
            { label: 'Personal growth on my own time' },
            { label: 'Academic reading for school' },
            { label: 'My company set this up for me' },
          ],
        },
        {
          id: 'thanks-for-input',
          type: 'Text',
          hideHeader: true,
          title: 'Thanks for the input!',
          subtitle:
            'Let’s make sure you’re set up for success with a quick walkthrough of how Shortform works.',
          image: {
            src: 'thanks-input-hero.svg',
            width: 375,
            height: 770,
          },
          textSize: 'large',
        },
        productDemoPage,
        learningSetupSummaryPage,
      ],
    },
  ]
}

/**
 * Default questionnaire pages without active experiment variants.
 * Do not import directly. Use it through `onboardingPages` below.
 */
export const questionnairePages: QuestionnaireSection[] =
  buildQuestionnairePages()

/**
 * Do not import directly. Use it through `onboardingPages` below. We export it for use in tests.
 */
export const trialPages: OnboardingTrialPage[] = [
  {
    title: 'How our Free Trial works',
    steps: [
      {
        text: 'Free Premium access to Shortform. No payment now',
        icon: 'lock.svg',
        day: 'Today',
      },
      {
        text: 'We will remind you before your trial ends',
        icon: 'bell.svg',
        day: 'Day 3',
      },
      {
        text: trialByPlanV2_18369Treatment()
          ? 'Cancel in advance to avoid payment'
          : 'Cancel your subscription to avoid a charge',
        icon: 'coin.svg',
        day: 'Day 5',
      },
    ],
  },
]

export const onboardingPages = {
  get intro(): OnboardingIntroPage[] {
    return []
  },
  get questionnaire(): QuestionnaireSection[] {
    return buildQuestionnairePages()
  },
  get trial(): OnboardingTrialPage[] {
    return trialPages
  },
}
